import { useMemo, useState } from 'react';
import { Button } from '../../atoms/Button/Button';
import { CalendarMonth } from '../../molecules/CalendarMonth/CalendarMonth';
import {
  addMonths,
  isSameDay,
  orderRange,
  startOfMonth,
  type DateRange,
} from '../../molecules/CalendarMonth/calendar';
import { DatePickerListItem } from '../../molecules/DatePickerListItem/DatePickerListItem';
import { Select } from '../../molecules/Select/Select';
import { useToday } from '../../provider/UIProvider';
import { NONE_PRESET_ID, defaultDatePickerPresets, type DatePickerPreset } from './presets';
import styles from './DatePickerMenu.module.css';

/**
 * Figma: `_Date picker menu` (123:2191) — "The floating calendar panel that
 * opens from the Date Picker Dropdown trigger. Supports dual-date (range) and
 * single-date modes. Variants: Type (Dual dates, Single date) × Breakpoint
 * (Desktop). Fill: White. Stroke: 1px light grey. Shadow: 3-layer drop shadow
 * elevation. Corner radius: 8px. Boolean props: Pre-set ranges sidebar, Actions
 * footer (Cancel/Apply)."
 *
 * The 2026-09-21 update added a `None` preset row and a `Clear selection`
 * button at the left of the dual-date footer (`Hierarchy=Ghost Color`). Both
 * are dual-date only — the single-date variant is unchanged.
 *
 * `Breakpoint` has a single option, `Desktop`, so it is not a prop — there is
 * nothing to switch between. The two booleans are `showPresets` and
 * `showActions`.
 *
 * The `_` prefix marks it internal in Figma, so it is not part of the public
 * package surface — compose it through `DatePicker`.
 */
export type DatePickerMenuType = 'dualDates' | 'singleDate';

export type DatePickerMenuProps = {
  /** Figma `Type`. */
  type?: DatePickerMenuType;
  value?: DateRange;
  onValueChange?: (value: DateRange) => void;
  /** Figma `Pre-set ranges`: the sidebar in dual mode, the `Select` in single. */
  presets?: DatePickerPreset[];
  showPresets?: boolean;
  /** Figma `Actions` — the Cancel / Apply footer. */
  showActions?: boolean;
  onApply?: () => void;
  onCancel?: () => void;
  /** The leftmost visible month. Dual mode draws this one and the next. */
  month?: Date;
  /** Where an uncontrolled menu opens. Defaults to the selection, else today. */
  defaultMonth?: Date;
  onMonthChange?: (month: Date) => void;
  isDateDisabled?: (date: Date) => boolean;
  locale?: string;
  className?: string;
};

const EMPTY: DateRange = { start: null, end: null };

/**
 * A click on a day. In single mode it replaces the date. In dual mode the first
 * click opens a new range and the second closes it, in whichever order the two
 * were clicked.
 */
export function nextRange(current: DateRange, day: Date, type: DatePickerMenuType): DateRange {
  if (type === 'singleDate') return { start: day, end: null };
  if (!current.start || current.end) return { start: day, end: null };
  return orderRange({ start: current.start, end: day });
}

export function DatePickerMenu({
  type = 'dualDates',
  value = EMPTY,
  onValueChange,
  presets,
  showPresets = true,
  showActions = true,
  onApply,
  onCancel,
  month,
  defaultMonth,
  onMonthChange,
  isDateDisabled,
  locale,
  className,
}: DatePickerMenuProps) {
  /*
   * The fallback presets are relative to "now", not to the selection — `Today`
   * has to mean today even when a past range is loaded. Memoised so the list
   * keeps its identity across renders.
   */
  const today = useToday();
  const fallbackPresets = useMemo(() => defaultDatePickerPresets(today), [today]);
  const allRows = presets ?? fallbackPresets;
  /* Figma's single-date `Select` does not offer `None`. */
  const rows = type === 'singleDate' ? allRows.filter((row) => row.id !== NONE_PRESET_ID) : allRows;

  /*
   * The visible month is uncontrolled unless the caller passes one, and it opens
   * on the selection so a saved range is on screen when the menu appears.
   */
  const [ownMonth, setOwnMonth] = useState(() =>
    startOfMonth(defaultMonth ?? value.start ?? today),
  );
  const visibleMonth = month ?? ownMonth;
  const setMonth = (next: Date) => {
    setOwnMonth(next);
    onMonthChange?.(next);
  };

  /*
   * Which preset row reads as `State=Active` — derived from the selection
   * rather than remembered, so clicking a day that happens to reconstruct a
   * preset lights it up, and picking any other day clears it.
   *
   * An empty selection is `None` and nothing else. `All time` can also be an
   * empty range (see `NONE_PRESET_ID`), so any other preset with no start never
   * matches — otherwise both rows would light at once.
   */
  const isEmpty = !value.start && !value.end;
  const presetId =
    rows.find((row) => {
      if (row.id === NONE_PRESET_ID) return isEmpty;
      const range = row.getValue();
      if (!range.start || !isSameDay(range.start, value.start)) return false;
      return type === 'singleDate' || isSameDay(range.end, value.end);
    })?.id ?? null;

  const selectDate = (day: Date) => {
    onValueChange?.(nextRange(value, day, type));
  };

  const applyPreset = (preset: DatePickerPreset) => {
    const range = preset.getValue();
    const next = type === 'singleDate' ? { start: range.start, end: null } : range;
    onValueChange?.(next);
    if (next.start) setMonth(startOfMonth(next.start));
  };

  const monthProps = {
    value,
    onSelectDate: selectDate,
    isDateDisabled,
    locale,
  };

  const actions = showActions ? (
    <div className={styles.actions}>
      <Button hierarchy="tertiary" onClick={onCancel}>
        Cancel
      </Button>
      <Button hierarchy="primary" onClick={onApply}>
        Apply
      </Button>
    </div>
  ) : null;

  if (type === 'singleDate') {
    return (
      <div className={[styles.menu, styles.single, className].filter(Boolean).join(' ')}>
        {showPresets && (
          <div className={styles.selectPanel}>
            <Select
              options={rows.map((row) => ({ value: row.id, label: row.label }))}
              value={presetId ?? undefined}
              onValueChange={(id) => {
                const preset = rows.find((row) => row.id === id);
                if (preset) applyPreset(preset);
              }}
              placeholder="Select a range"
            />
          </div>
        )}

        <div className={styles.singleBody}>
          <CalendarMonth
            {...monthProps}
            month={visibleMonth}
            onPrevMonth={() => setMonth(addMonths(visibleMonth, -1))}
            onNextMonth={() => setMonth(addMonths(visibleMonth, 1))}
          />
        </div>

        {showActions && <div className={[styles.footer, styles.footerFill].join(' ')}>{actions}</div>}
      </div>
    );
  }

  return (
    <div className={[styles.menu, styles.dual, className].filter(Boolean).join(' ')}>
      {showPresets && (
        <div className={styles.presets}>
          <ul className={styles.presetList}>
            {rows.map((row) => (
              <li key={row.id}>
                <DatePickerListItem
                  label={row.label}
                  selected={row.id === presetId}
                  onClick={() => applyPreset(row)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.trailing}>
        <div className={styles.pickers}>
          {/*
           * Figma gives the left calendar the "previous" arrow and the right one
           * "next", each balanced by a spacer on the other side. The two months
           * page together, which is what keeps them adjacent.
           */}
          <div className={[styles.picker, styles.pickerDivided].join(' ')}>
            <CalendarMonth
              {...monthProps}
              month={visibleMonth}
              nextSlot="spacer"
              onPrevMonth={() => setMonth(addMonths(visibleMonth, -1))}
            />
          </div>
          <div className={styles.picker}>
            <CalendarMonth
              {...monthProps}
              month={addMonths(visibleMonth, 1)}
              prevSlot="spacer"
              onNextMonth={() => setMonth(addMonths(visibleMonth, 1))}
            />
          </div>
        </div>

        {showActions && (
          <div className={[styles.footer, styles.footerSplit].join(' ')}>
            {/*
             * Figma `Bottom panel`'s leading button. Clears the draft only —
             * the calendar and the preset highlight empty, and nothing is
             * committed until Apply; Cancel still restores what was applied.
             */}
            <Button hierarchy="ghostColor" onClick={() => onValueChange?.(EMPTY)}>
              Clear selection
            </Button>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
